/**
 * NavigationContext - Provides navigation helpers to sidebar
 *
 * Solves the issue where DesktopSidebar is outside Tab.Navigator context
 * and can't directly navigate to tab screens
 */

import React, { createContext, useContext, ReactNode } from 'react';

export interface NavigationHelpers {
  navigateToHome: () => void;
  navigateToResearch: () => void;
  navigateToWatchlist: () => void;
  navigateToGroups: () => void;
  navigateToGroupChat: (groupId: string, groupName: string) => void;
  navigateToFeedback: () => void;
  navigateToProfile: () => void;
  getCurrentRoute: () => string;
  // Current navigation state
  currentTabRoute: string; // Current tab: "Home", "Groups", "Watchlist", etc.
  currentGroupId: string | null; // Current group ID if in GroupChat screen
}

const NavigationContext = createContext<NavigationHelpers | null>(null);

interface NavigationProviderProps {
  children: ReactNode;
  helpers: NavigationHelpers;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children, helpers }) => {
  return <NavigationContext.Provider value={helpers}>{children}</NavigationContext.Provider>;
};

export const useNavigationHelpers = (): NavigationHelpers => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigationHelpers must be used within NavigationProvider');
  }
  return context;
};
